import { useState } from 'react'
import { api } from '../../utils/api'
import { FORM_STYLE, ERROR_STYLE, SUCCESS_STYLE } from './orderEditStyles'

/**
 * OrderAddressEdit: the delivery address of the Shipping section, with an Edit
 * address button on an order that has not shipped. Editing turns it into a form.
 * Prism saves the address, writes it to Shopify for a Shopify order and logs the
 * change under the staff member's name. A printrun slip made earlier still shows
 * the old address, so the form and the result both remind staff to reprint it.
 *
 *   address      shipping_address from the order detail
 *                { shipping_name, company, address1, address2, city, province, zip, country_code, phone }
 *   printrun     the printrun holding the order, or null
 *   canEdit      true while the order has not shipped
 *   onSaved      (result) => void, called after Prism accepted the change
 */

const COMMON_COUNTRIES = ['GB', 'IE', 'FR', 'DE', 'NL', 'BE', 'ES', 'IT', 'US', 'CA', 'AU']
const ISO2_PATTERN = /^[A-Za-z]{2}$/
const SAVED_IN_PRISM_ONLY = {
  ebay: 'Saved in Prism only: change it in eBay too',
  reverb: 'Saved in Prism only: change it in Reverb too'
}
const SAVED_TO_SHOPIFY = 'Saved to Shopify as well · logged with your name'

const GRID_STYLE = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 12 }
const HEADING_STYLE = { fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }
const CHIP_STYLE = { fontSize: '0.75rem', background: '#FFF1C2', color: '#8a5a00', borderRadius: 999, padding: '3px 10px' }
const REMINDER_STYLE = { ...SUCCESS_STYLE, background: '#FFF8E1', border: '1px solid #FFD44D' }

export default function OrderAddressEdit({ marketplace, orderNumber, address, printrun, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [fields, setFields] = useState({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  const savedToShopify = marketplace === 'shopify'
  const required = [fields.name, fields.line1, fields.city, fields.postcode, fields.country]
  const canSave = required.every((value) => (value || '').trim().length > 0) && !busy
  const printrunLabel = printrun ? `#${String(printrun.id).slice(0, 8)}` : null

  function openForm() {
    setFields(startingFields(address))
    setError(null)
    setResult(null)
    setEditing(true)
  }

  function setField(name) {
    return (e) => setFields((current) => ({ ...current, [name]: e.target.value }))
  }

  async function save(e) {
    e.preventDefault()
    if (!canSave) return
    setBusy(true)
    setError(null)
    try {
      const saved = await api.put(`/api/orders/${marketplace}/${orderNumber}/address`, requestBody(fields))
      setResult(saved)
      setEditing(false)
      onSaved(saved)
    } catch (err) {
      setError(err.message || 'Could not change the address')
    } finally {
      setBusy(false)
    }
  }

  function textField(name, label, props = {}) {
    return (
      <div>
        <label htmlFor={`address-${name}`} className="filter-label">{label}</label>
        <input
          id={`address-${name}`}
          className="form-input"
          value={fields[name] || ''}
          onChange={setField(name)}
          disabled={busy}
          {...props}
        />
      </div>
    )
  }

  if (editing) {
    return (
      <form onSubmit={save} style={FORM_STYLE}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={HEADING_STYLE}>Delivery address · editing</span>
          {printrunLabel && <span style={CHIP_STYLE}>In printrun {printrunLabel}: reprint the slip after saving</span>}
        </div>
        <div style={GRID_STYLE}>
          {textField('name', 'Name', { autoFocus: true })}
          {textField('company', 'Company or building')}
          {textField('line1', 'Address line 1')}
          {textField('line2', 'Address line 2', { placeholder: 'Flat, floor' })}
          {textField('city', 'Town')}
          {textField('postcode', 'Postcode')}
          {textField('region', 'County or state')}
          <div>
            <label htmlFor="address-country" className="filter-label">Country</label>
            <select id="address-country" className="form-input" value={fields.country || ''} onChange={setField('country')} disabled={busy}>
              {countryOptions(fields.country).map(({ code, name }) => <option key={code} value={code}>{name}</option>)}
            </select>
          </div>
          {textField('phone', 'Phone')}
        </div>
        {error && <div role="alert" style={{ ...ERROR_STYLE, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
            {savedToShopify ? SAVED_TO_SHOPIFY : SAVED_IN_PRISM_ONLY[marketplace]}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!canSave}>{busy ? 'Saving…' : 'Save address'}</button>
          </span>
        </div>
      </form>
    )
  }

  return (
    <>
      <div className="order-detail-item">
        <span className="order-detail-label">Shipping Address</span>
        <span className="order-detail-value">
          {address ? <AddressLines address={address} /> : '—'}
          {canEdit && (
            <div style={{ marginTop: 8 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={openForm}>Edit address</button>
            </div>
          )}
        </span>
      </div>
      {result && <div role="status" style={SUCCESS_STYLE}>{result.message}</div>}
      {result?.in_printrun && (
        <div role="status" style={REMINDER_STYLE}>
          Reprint the slip: this order is in printrun #{String(result.in_printrun.id).slice(0, 8)} and the printed slip shows the old address.
        </div>
      )}
    </>
  )
}

function AddressLines({ address }) {
  const region = address.province || address.province_code
  return (
    <>
      {address.shipping_name && <>{address.shipping_name}<br /></>}
      {address.company && <>{address.company}<br /></>}
      {address.address1}<br />
      {address.address2 && <>{address.address2}<br /></>}
      {address.city}, {region} {address.zip}<br />
      {address.country_code || address.country}
    </>
  )
}

function startingFields(address) {
  const code = address?.country_code || ''
  return {
    name: address?.shipping_name || '',
    company: address?.company || '',
    line1: address?.address1 || '',
    line2: address?.address2 || '',
    city: address?.city || '',
    region: address?.province || '',
    postcode: address?.zip || '',
    country: ISO2_PATTERN.test(code) ? code.toUpperCase() : '',
    phone: address?.phone || ''
  }
}

function requestBody(fields) {
  return {
    name: fields.name.trim(),
    company: fields.company.trim(),
    line1: fields.line1.trim(),
    line2: fields.line2.trim(),
    city: fields.city.trim(),
    region: fields.region.trim(),
    postcode: fields.postcode.trim(),
    country: fields.country,
    phone: fields.phone.trim()
  }
}

function countryOptions(current) {
  const codes = current && !COMMON_COUNTRIES.includes(current) ? [current, ...COMMON_COUNTRIES] : COMMON_COUNTRIES
  const names = new Intl.DisplayNames(['en-GB'], { type: 'region' })
  const options = codes.map((code) => ({ code, name: names.of(code) }))
  return current ? options : [{ code: '', name: 'Choose a country' }, ...options]
}
